import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Mail, Lock, User, Loader2, CheckCircle, AlertCircle, AtSign, Phone } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, register, initiateGoogleLogin, isAuthenticated } = useAuth();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [phone, setPhone] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Check for messages from other pages
  useEffect(() => {
    if (location.state?.error) {
      setError(location.state.error);
    }
    if (location.state?.success) {
      setSuccess(location.state.success);
    }
  }, [location.state]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setSuccess('');

    if (isSignUp) {
      const result = await register(email, name, password, username, phone);
      setIsLoading(false);
      
      if (result.success) {
        if (result.requiresVerification) {
          setSuccess(result.message);
          // Clear form
          setEmail('');
          setName('');
          setPassword('');
          setUsername('');
          setPhone('');
        } else {
          navigate('/explore');
        }
      } else {
        setError(result.error);
      }
    } else {
      const result = await login(email, password);
      setIsLoading(false);
      
      if (result.success) {
        navigate('/');
      } else {
        setError(result.error);
      }
    }
  };

  const handleGoogleLogin = () => {
    setSocialLoading('google');
    initiateGoogleLogin();
  };

  const handleSocialLogin = async (provider) => {
    if (provider === 'google') {
      handleGoogleLogin();
      return;
    }
    
    // Show info for unsupported providers
    setError(`${provider === 'microsoft' ? 'Microsoft' : 'Facebook'} girişi şu an aktif değil. Lütfen Google ile giriş yapın veya e-posta ile kayıt olun.`);
  };

  return (
    <div className="min-h-screen bg-[#e8f4f8] flex items-center justify-center p-4">
      {/* Decorative circles */}
      <div className="fixed top-0 left-0 w-96 h-96 bg-[#d0e8ef] rounded-full -translate-x-1/2 -translate-y-1/2 opacity-50" />
      <div className="fixed bottom-0 right-0 w-80 h-80 bg-[#d0e8ef] rounded-full translate-x-1/3 translate-y-1/3 opacity-50" />
      <div className="fixed top-1/2 right-0 w-64 h-64 bg-[#d0e8ef] rounded-full translate-x-1/2 opacity-30" />
      
      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md bg-white rounded-3xl shadow-xl p-8 py-10">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 rounded-full bg-gradient-to-br from-[#2d1b4e] to-[#1a1040] flex items-center justify-center shadow-lg overflow-hidden">
            <img 
              src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/692cc1ea9c8a6553034e2bc3/40c4882f7_logo.png" 
              alt="Giresun Kent Rehberi" 
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-center text-gray-900 mb-1">
          Giresun Kent Rehberi'ne Hoş Geldiniz
        </h1>
        <p className="text-center text-gray-500 mb-8">
          {isSignUp ? 'Yeni hesap oluşturun' : 'Hesabınıza giriş yapın'}
        </p>

        {/* Success Message */}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 text-green-700 text-sm rounded-xl flex items-start gap-3">
            <CheckCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <span>{success}</span>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-600 text-sm rounded-xl flex items-start gap-3">
            <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Social Login Buttons */}
        {!isSignUp && (
          <>
            <div className="space-y-3 mb-6">
              <button
                onClick={() => handleGoogleLogin()}
                disabled={socialLoading !== ''}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors duration-200 text-gray-700 font-medium disabled:opacity-50"
              >
                {socialLoading === 'google' ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                )}
                {socialLoading === 'google' ? 'Yönlendiriliyor...' : 'Google ile devam et'}
              </button>

              <button
                onClick={() => handleSocialLogin('microsoft')}
                disabled={socialLoading !== ''}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors duration-200 text-gray-400 font-medium disabled:opacity-50 cursor-not-allowed"
              >
                <svg className="w-5 h-5 opacity-50" viewBox="0 0 23 23">
                  <path fill="#f35325" d="M1 1h10v10H1z"/>
                  <path fill="#81bc06" d="M12 1h10v10H12z"/>
                  <path fill="#05a6f0" d="M1 12h10v10H1z"/>
                  <path fill="#ffba08" d="M12 12h10v10H12z"/>
                </svg>
                Microsoft (Yakında)
              </button>

              <button
                onClick={() => handleSocialLogin('facebook')}
                disabled={socialLoading !== ''}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-gray-200 rounded-xl hover:bg-gray-50 transition-colors duration-200 text-gray-400 font-medium disabled:opacity-50 cursor-not-allowed"
              >
                <svg className="w-5 h-5 opacity-50" viewBox="0 0 24 24" fill="#1877F2">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
                Facebook (Yakında)
              </button>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-4 mb-6">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400 uppercase tracking-wider">veya</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>
          </>
        )}

        {/* Email/Password Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {isSignUp && (
            <>
              <div>
                <label className="block text-center text-sm font-medium text-gray-700 mb-2">
                  Ad Soyad
                </label>
                <div className="relative">
                  <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="Adınız Soyadınız"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required={isSignUp}
                    className="pl-12 py-3 h-12 rounded-xl border-gray-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]"
                  />
                </div>
              </div>
              <div>
                <label className="block text-center text-sm font-medium text-gray-700 mb-2">
                  Kullanıcı Adı <span className="text-gray-400 font-normal">(opsiyonel)</span>
                </label>
                <div className="relative">
                  <AtSign className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="text"
                    placeholder="kullaniciadi"
                    value={username}
                    onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                    className="pl-12 py-3 h-12 rounded-xl border-gray-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]"
                  />
                </div>
              </div>
              <div>
                <label className="block text-center text-sm font-medium text-gray-700 mb-2">
                  Telefon <span className="text-gray-400 font-normal">(opsiyonel)</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <Input
                    type="tel"
                    placeholder="05XX XXX XX XX"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="pl-12 py-3 h-12 rounded-xl border-gray-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]"
                  />
                </div>
              </div>
            </>
          )}

          <div>
            <label className="block text-center text-sm font-medium text-gray-700 mb-2">
              E-posta
            </label>
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="email"
                placeholder="ornek@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="pl-12 py-3 h-12 rounded-xl border-gray-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]"
              />
            </div>
          </div>

          <div>
            <label className="block text-center text-sm font-medium text-gray-700 mb-2">
              Şifre
            </label>
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                className="pl-12 py-3 h-12 rounded-xl border-gray-200 focus:border-[#1e3a5f] focus:ring-[#1e3a5f]"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={isLoading}
            className="w-full h-12 bg-[#1e3a5f] hover:bg-[#2a4a73] text-white font-medium rounded-xl transition-colors duration-200"
          >
            {isLoading ? (
              <span className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                {isSignUp ? 'Kayıt yapılıyor...' : 'Giriş yapılıyor...'}
              </span>
            ) : (
              isSignUp ? 'Kayıt Ol' : 'Giriş Yap'
            )}
          </Button>
        </form>

        {/* Footer Links */}
        <div className="mt-6 text-center text-sm space-y-2">
          {!isSignUp && (
            <div>
              <Link to="/forgot-password" className="text-[#1e3a5f] hover:underline">
                Şifremi unuttum
              </Link>
            </div>
          )}
          <div>
            <span className="text-gray-500">
              {isSignUp ? 'Zaten hesabınız var mı? ' : 'Hesabınız yok mu? '}
            </span>
            <button 
              onClick={() => {
                setIsSignUp(!isSignUp);
                setError('');
                setSuccess('');
              }}
              className="text-[#1e3a5f] hover:underline font-medium"
            >
              {isSignUp ? 'Giriş Yap' : 'Kayıt Ol'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
